import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { client, APIError } from "@/api"
import type { Affiliate } from "./useAffiliates"

interface ReferredUser {
  public_id: string
  name: string
  email: string
  created_at: string
}

interface AffiliateTotals {
  totalOwedCents: number
  totalPaidCents: number
  totalEarnedCents: number
}

interface AffiliateDetailResponse {
  // `has_referrals`: someone has signed up through it (or a credit, pin or statement exists), so
  // its commission model, and a first-year-net rate and window, can no longer be edited.
  affiliate: Omit<Affiliate, "totalReferred" | "totalOwedCents"> & { has_referrals: boolean }
  referredUsers: ReferredUser[]
  // True count, NOT referredUsers.length, the array is capped (recent-signups preview).
  referredUsersTotalCount: number
  totals: AffiliateTotals
}

export function useAffiliateDetail(publicId: string) {
  return useQuery<AffiliateDetailResponse>({
    queryKey: ["affiliate", publicId],
    queryFn: async () => {
      const res = await client.admin.affiliates[":publicId"].$get({ param: { publicId } })
      if (!res.ok) {
        throw new Error("Failed to fetch affiliate")
      }
      return res.json()
    },
    enabled: !!publicId,
  })
}

export interface AffiliateCredit {
  public_id: string
  stripe_subscription_id: string
  commission_percent: number
  gross_amount_cents: number
  commission_amount_cents: number
  status: "pending" | "paid" | "voided"
  voided_at: string | null
  void_reason: string | null
  created_at: string
  updated_at: string
}

interface AffiliateCreditsResponse {
  credits: AffiliateCredit[]
  pagination: {
    page: number
    pageSize: number
    totalCount: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

export function useAffiliateCredits(
  publicId: string,
  params: { page?: number; pageSize?: number; status?: "pending" | "paid" | "voided" } = {}
) {
  const { page = 1, pageSize = 50, status } = params
  return useQuery<AffiliateCreditsResponse>({
    queryKey: ["affiliate-credits", publicId, page, pageSize, status],
    queryFn: async () => {
      const res = await client.admin.affiliates[":publicId"].credits.$get({
        param: { publicId },
        query: { page, pageSize, status },
      })
      if (!res.ok) {
        throw new Error("Failed to fetch affiliate credits")
      }
      return res.json()
    },
    enabled: !!publicId,
  })
}

export type MarkAffiliatePaidPayload = {
  publicId: string
  creditPublicIds: string[]
  method: "check" | "ach" | "paypal" | "wire" | "other"
  reference?: string
  adminNotes?: string
}

export function useMarkAffiliatePaid() {
  const qc = useQueryClient()
  return useMutation<{ payment: unknown }, APIError, MarkAffiliatePaidPayload>({
    mutationFn: async ({ publicId, ...payload }) => {
      const res = await client.admin.affiliates[":publicId"].payments.$post({
        param: { publicId },
        json: payload,
      })
      if (!res.ok) {
        const err = (await res.clone().json().catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "AFFILIATE_MARK_PAID_FAILED",
          new Error(err.message || "Failed to mark credits as paid")
        )
      }
      return (await res.json()) as { payment: unknown }
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["affiliate", variables.publicId] })
      qc.invalidateQueries({ queryKey: ["affiliate-credits", variables.publicId] })
      qc.invalidateQueries({ queryKey: ["affiliates"] })
      qc.invalidateQueries({ queryKey: ["affiliate-payments"] })
    },
  })
}

export type SendReferralEmailPayload = {
  publicId: string
  recipientEmail: string
  personalNote?: string
}

export function useSendReferralEmail() {
  return useMutation<{ success: boolean }, APIError, SendReferralEmailPayload>({
    mutationFn: async ({ publicId, ...payload }) => {
      const res = await client.admin.affiliates[":publicId"]["send-referral-email"].$post({
        param: { publicId },
        json: payload,
      })
      if (!res.ok) {
        const err = (await res.clone().json().catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "AFFILIATE_SEND_REFERRAL_EMAIL_FAILED",
          new Error(err.message || "Failed to send the referral email")
        )
      }
      return (await res.json()) as { success: boolean }
    },
  })
}

// Additional/secondary link variants for one affiliate, e.g. one code per channel
// ("Instagram bio", "email signature"). All attribute to the SAME affiliate as the
// primary ref_code. The primary code lives on `Affiliate` itself, not here.
export interface AffiliateRefCode {
  public_id: string
  ref_code: string
  label: string | null
  status: "active" | "inactive"
  created_at: string
  updated_at: string
  referralUrl: string
}

export function useAffiliateRefCodes(publicId: string) {
  return useQuery<{ refCodes: AffiliateRefCode[] }>({
    queryKey: ["affiliate-ref-codes", publicId],
    queryFn: async () => {
      const res = await client.admin.affiliates[":publicId"]["ref-codes"].$get({
        param: { publicId },
      })
      if (!res.ok) {
        throw new Error("Failed to fetch additional referral links")
      }
      return res.json()
    },
    enabled: !!publicId,
  })
}

export type AddAffiliateRefCodePayload = {
  publicId: string
  refCode: string
  label?: string
}

export function useAddAffiliateRefCode() {
  const qc = useQueryClient()
  return useMutation<{ refCode: AffiliateRefCode }, APIError, AddAffiliateRefCodePayload>({
    mutationFn: async ({ publicId, ...payload }) => {
      const res = await client.admin.affiliates[":publicId"]["ref-codes"].$post({
        param: { publicId },
        json: payload,
      })
      if (!res.ok) {
        const err = (await res.clone().json().catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "AFFILIATE_REF_CODE_ADD_FAILED",
          new Error(err.message || "Failed to add referral link")
        )
      }
      return (await res.json()) as { refCode: AffiliateRefCode }
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["affiliate-ref-codes", variables.publicId] })
    },
  })
}

export type UpdateAffiliateRefCodePayload = {
  publicId: string
  refCodePublicId: string
  label?: string | null
  status?: "active" | "inactive"
}

export function useUpdateAffiliateRefCode() {
  const qc = useQueryClient()
  return useMutation<{ refCode: AffiliateRefCode }, APIError, UpdateAffiliateRefCodePayload>({
    mutationFn: async ({ publicId, refCodePublicId, ...payload }) => {
      const res = await client.admin.affiliates[":publicId"]["ref-codes"][
        ":refCodePublicId"
      ].$patch({
        param: { publicId, refCodePublicId },
        json: payload,
      })
      if (!res.ok) {
        const err = (await res.clone().json().catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "AFFILIATE_REF_CODE_UPDATE_FAILED",
          new Error(err.message || "Failed to update referral link")
        )
      }
      return (await res.json()) as { refCode: AffiliateRefCode }
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["affiliate-ref-codes", variables.publicId] })
    },
  })
}
